import { useState } from 'react';
import type { LegendItem } from './types';

interface LegendProps {
  items: LegendItem[];
}

/**
 * Legend component that displays color-to-category mappings
 * Shows an icon in the bottom right corner that reveals legend on hover or focus
 */
export function Legend({ items }: LegendProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const isVisible = isHovered || isFocused;

  if (items.length === 0) {
    return null;
  }

  return (
    <div className="relative" data-export-target="legend">
      <button
        type="button"
        aria-label="Legend"
        aria-haspopup="true"
        aria-expanded={isVisible}
        className="flex items-center justify-center w-10 h-10 bg-bg-brand hover:bg-bg-brand-hovered rounded-full shadow-lg cursor-pointer transition-colors"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
      >
        <svg
          className="w-5 h-5 text-text-neutral-inverted"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </button>

      {/* Legend Popover */}
      {isVisible && (
        <div className="absolute bottom-full right-0 mb-2 bg-be-neutral-subtle rounded-lg shadow-xl border border-border-neutral-subtle p-4 min-w-[200px] z-50">
          <h3 className="text-sm font-semibold text-text-neutral-primary mb-3">Legend</h3>
          <div className="space-y-2">
            {items.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <div
                  aria-hidden="true"
                  className="w-4 h-4 rounded shrink-0"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-sm text-text-neutral-secondary">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
